import ArticleLayout from '../../components/ArticleLayout';
import TermostatSnitFigure from '../../components/figures/TermostatSnitFigure';

export default function RadiatorTermostat() {
  return (
    <ArticleLayout
      title="Radiatortermostater forklaret: Typer, fjernføler og levetid"
      metaTitle="Radiatortermostat: Typer, fjernføler og levetid"
      metaDescription="Alt om radiatortermostater: mekaniske følere med gas eller væske, elektroniske wifi-termostater, fjernfølere og hvornår termostaten er slidt op."
      canonicalPath="/radiator-termostat"
      introLead="En radiatortermostat er ikke en simpel vandhane, men en selvstændig, mekanisk regulator, der konstant måler rumtemperaturen og tilpasser vandmængden automatisk."
      introAnswer="De fleste danske hjem har traditionelle mekaniske termostater med en gas- eller væskefyldt bælg. Efter 10–15 års trofast drift mister bælgen gradvist sin spændstighed, hvilket fører til overforbrug og upræcis temperaturstyring. Udskiftning af selve termostathovedet kan gøres helt uden værktøj og uden vandudslip."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: Er termostaten slidt op?' },
        { id: 'typer', title: '2. Termostattyper: Mekanisk, elektronisk og wifi' },
        { id: 'fjernfoeler', title: '3. Hvorfor og hvornår vælges en fjernføler?' },
        { id: 'skift', title: '4. Sådan skiftes et termostathoved' },
        { id: 'faq', title: '5. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: 5 tegn på at din termostat bør udskiftes"
      checklist={[
        {
          title: 'Radiatoren er brændende varm, selvom den er sat på 1 eller 2',
          description: 'Dette afslører, at følerelementet har mistet sit tryk og ikke længere kan overvinde ventilens returfjeder.',
        },
        {
          title: 'Termostathovedet er over 12–15 år gammelt',
          description: 'Danfoss og energirådgivere anbefaler udskiftning efter ca. 12–15 år for at opretholde nøjagtig temperaturregulering.',
        },
        {
          title: 'Drejehjulet går ualmindeligt stramt eller knirker',
          description: 'Slidte plastikgevind og indvendige spændbånd gør det umuligt at finindstille rumvarmen.',
        },
        {
          title: 'Radiatoren reagerer ikke på solindfald eller mange gæster',
          description: 'En sund termostat lukker automatisk, når rummet tilføres gratis varme fra solen eller mennesker.',
        },
        {
          title: 'Plasten er mørnet, gulnet eller revnet omkring snapkoblingen',
          description: 'Hvis snap-koblingen eller spændebåndet giver sig, sidder stiften ikke i korrekt anlæg mod ventilen.',
        },
      ]}
      diagram={<TermostatSnitFigure />}
      sections={[
        {
          id: 'typer',
          title: 'Termostattyper: Gas, væske og smarte elektroniske modeller',
          content: (
            <>
              <p>
                Der findes grundlæggende tre kategorier af termostater på det danske marked i dag:
              </p>
              <p>
                <strong>1. Gasfyldte termostater (fx Danfoss RA/Aero):</strong> Indeholder en lille metalkapsel med gas. Gas reagerer lynhurtigt på temperaturændringer i luften – typisk på under 10–12 minutter – og er den mest præcise mekaniske termostat på markedet.
              </p>
              <p>
                <strong>2. Væskefyldte termostater:</strong> Anvender en ekspanderende mineralsk væske. De har en lidt langsommere reaktionstid (typisk 18–25 minutter), men er særdeles robuste og udbredte.
              </p>
              <p>
                <strong>3. Elektroniske og trådløse (wifi/Zigbee) termostater:</strong> Drevet af små batterier med en digital stepmotor. De muliggør præcise ugeprogrammer, automatisk natsænkning og styring fra smartphone. De sparer typisk 5–10 % yderligere energi i boliger med variabel tilstedeværelse.
              </p>
            </>
          ),
        },
        {
          id: 'fjernfoeler',
          title: 'Fjernføler: Løsningen ved gardiner og radiatorskjulere',
          content: (
            <>
              <p>
                En standardtermostat har føleren indbygget direkte i det hvide drejehoved. Det forudsætter, at luften i rummet kan cirkulere frit omkring termostaten.
              </p>
              <p>
                Hvis radiatoren er placeret bag et kraftigt gulvlangt gardin, i en dyb niche under en bred vinduesplade eller inde bag en radiatorskjuler, opstår der en varme-lomme. Termostaten måler måske 24 °C inde bag gardinet og lukker omgående for varmen – mens stuen reelt kun er 17 °C.
              </p>
              <p>
                Løsningen er en termostat med fjernføler. Den består af et tyndt kapillarrør (1–2 meter langt), som forbinder drejehovedet med en lille følerkapsel, der monteres frit på væggen ved siden af radiatoren eller gardinet.
              </p>
            </>
          ),
        },
        {
          id: 'skift',
          title: 'Sådan skifter du selve termostathovedet',
          content: (
            <>
              <p>
                At udskifte selve termostathovedet (føleren) er et helt tørt arbejde. Du rører ikke ved vandet og behøver hverken lukke for hovedhanen eller tappe anlægget:
              </p>
              <p>
                <strong>Danfoss med snapkobling:</strong> Drej termostaten på maksimal åbning. Grib fat om kraven bag drejehovedet og drej mod uret (eller løsn en lille unbrakoskrue/klemring), hvorefter hovedet trækkes lige ud.
              </p>
              <p>
                <strong>Montering af ny føler:</strong> Sæt den nye termostat på fuld åbning (trin 5), pres den lige ind over ventilhalsen, og klik eller spænd kraven fast.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Hvornår skal ventilen inde i røret også skiftes?',
        content: (
          <p>
            Hvis du monterer et spritnyt termostathoved, og radiatoren stadig ikke varmer (eller aldrig slukker), skyldes fejlen som regel selve messingventilen inde i røret. Udskiftning af ventilhuset kræver autoriseret VVS-arbejde, da rørene skal trykaflastes eller fryses.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Passer alle termostater på alle radiatorer?',
          a: 'Nej. Fastgørelsen varierer. Ældre Danfoss bruger RAV (34 mm spændebånd), RAVL (26 mm) eller RA 2000 (snap-kobling). Mange andre mærker bruger standard M30 x 1,5 mm gevind. Tjek ventilens hals før køb.',
        },
        {
          q: 'Kan man spare penge ved at skifte gamle termostater?',
          a: 'Ja. Ifølge Energistyrelsen og Danfoss sparer udskiftning af 15–20 år gamle termostater til moderne præcise modeller typisk 4–8 % på det samlede varmeforbrug.',
        },
        {
          q: 'Hvad betyder stjernen (*) på termostaten?',
          a: 'Stjernen markerer frostsikring. Den holder radiatoren lukket, indtil rumtemperaturen falder til omkring 7–8 °C, hvor den åbner for at forhindre sprængte rør.',
        },
      ]}
      internalLinks={[
        { to: '/radiator-termostat-indstilling', text: 'Indstilling af termostat i grader', desc: 'hvad betyder tallene på skalaen?' },
        { to: '/radiator-ventil-sidder-fast', text: 'Ventilstiften sidder fast', desc: 'sådan frigøres stiften bag termostathovedet' },
        { to: '/spar-paa-varmen', text: 'Spar på varmen med korrekt indstilling', desc: 'Danfoss og HOFORs bedste råd' },
        { to: '/radiator-udskiftning-pris', text: 'Pris på udskiftning af termostat', desc: 'overslag over reservedele og VVS-arbejde' },
        { to: '/radiator-varmer-selvom-den-er-slukket', text: 'Radiatoren varmer, selvom den er slukket', desc: 'når termostaten ikke lukker' },
      ]}
      sources={[
        { url: 'https://www.danfoss.com/da-dk/products/dhs/radiator-and-room-thermostats/radiator-thermostats/mechanical-radiator-thermostats/', label: 'Danfoss Aveo™ mekanisk radiatortermostat | Danfoss' },
        { url: 'https://www.danfoss.com/da-dk/industries/buildings-residential/dhs/solutions-for-homeowners/saving-energy/', label: 'Gode råd til at spare på varmen | Danfoss' },
      ]}
    />
  );
}
